import { useState, type MouseEvent } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import type { CuratedPlaceOfSafety } from "@thalamos/common";

import { CreateEditPosModal } from "./CreateEditPlaceOfSafetyModal.js";
import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export function DeletePosModal({
  open,
  onClose,
  pos,
  handleSubmit,
}: {
  open: boolean;
  onClose: () => void;
  pos: CuratedPlaceOfSafety;
  handleSubmit: () => Promise<void>;
}) {
  const { t } = useTranslation();
  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("pages.posManagement.deleteModal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>{t("pages.posManagement.deleteModal.content")}</Typography>
        <Typography sx={{ fontWeight: "bold", margin: "2em 0" }}>
          {pos.name}
          <br></br>
          {pos.address}
          <br></br>
          {pos.postalCode}
        </Typography>
        <Banner
          bannerType={BannerList.WARNING}
          body={t("pages.posManagement.deleteModal.warning")}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", marginTop: "1em" }}>
        <Button
          label={t("buttonLabels.cancel")}
          onClick={onClose}
          variant="outlined"
        />
        <Button
          label={t("pages.posManagement.deleteModal.confirm")}
          onClick={handleSubmit}
          color={"error"}
        />
      </DialogActions>
    </PopupDialog>
  );
}

function PosManagementRow({
  pos,
  refetchPos,
}: {
  pos: CuratedPlaceOfSafety;
  refetchPos: () => {};
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeletePosModal, setDeletePosModal] = useState(false);
  const [showCreateEditPosModal, setShowCreateEditPosModal] = useState(false);
  const { t } = useTranslation();
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <TableRow key={pos.id}>
        <TableCell>{pos.name}</TableCell>
        <TableCell>{pos.address}</TableCell>
        <TableCell>{pos.postalCode}</TableCell>
        <TableCell>{pos.posType}</TableCell>
        <TableCell>{pos.odsCode}</TableCell>
        <TableCell>{pos.trustName}</TableCell>
        <TableCell>
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{
              flexWrap: "wrap",
            }}
          >
            {pos.config?.s136ExtensionFormEnabled && (
              <Chip
                label={t("pages.posManagement.table.s136Extension")}
                size="small"
                color="primary"
              />
            )}
            {pos.config?.outcomeFormEnabled && (
              <Chip
                label={t("pages.posManagement.table.outcomeForm")}
                size="small"
                color="primary"
              />
            )}
            {!pos.config?.s136ExtensionFormEnabled &&
              !pos.config?.outcomeFormEnabled && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                  }}
                >
                  —
                </Typography>
              )}
          </Stack>
        </TableCell>
        <TableCell>
          <Button variant="outlined" onClick={handleClick} label="Action" />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              sx={{
                textTransform: "uppercase",
                color: "primary.main",
              }}
              onClick={() => {
                setShowCreateEditPosModal(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              {t("pages.posManagement.table.edit")}
            </MenuItem>

            <MenuItem
              sx={{
                textTransform: "uppercase",
                color: "primary.main",
              }}
              onClick={() => {
                setDeletePosModal(true);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="primary" />
              </ListItemIcon>
              {t("pages.posManagement.table.delete")}
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      {showDeletePosModal && (
        <DeletePosModal
          open={showDeletePosModal}
          onClose={() => {
            setDeletePosModal(false);
          }}
          pos={pos}
          handleSubmit={async () => {
            try {
              const result = await api.placeOfSafety.delete(pos.id);
              if (result.status !== 204) {
                renderErrorToast({
                  message: t("pages.posManagement.deleteModal.errorMessage"),
                });
              } else {
                renderSuccessToast({
                  message: t("pages.posManagement.deleteModal.successMessage", {
                    posName: pos.name,
                  }),
                });
              }
            } catch {
              renderErrorToast({
                message: t("pages.posManagement.deleteModal.errorMessage"),
              });
            }
            setDeletePosModal(false);
            refetchPos();
          }}
        />
      )}
      {showCreateEditPosModal && (
        <CreateEditPosModal
          open={showCreateEditPosModal}
          onClose={() => {
            setShowCreateEditPosModal(false);
          }}
          refetchPos={refetchPos}
          pos={pos}
        />
      )}
    </>
  );
}

export function PlaceOfSafetyManagementPage() {
  const [posFilterQuery, setPosFilterQuery] = useState("");
  const [showCreateEditPosModal, setShowCreateEditPosModal] = useState(false);
  const [page, setPage] = useState(0);
  const { t } = useTranslation();
  const posPageSize = 10;

  const [{ data: pos }, refetchPos] = apiHooks.placeOfSafety.search(
    posFilterQuery,
    posPageSize,
    page * posPageSize,
  );

  const numPages = pos && pos.count ? Math.ceil(pos.count / posPageSize) : 1;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          marginTop: "1em",
          marginBottom: "1em",
        }}
      >
        <TextField
          placeholder={"Filter pos..."}
          sx={{ width: "100%" }}
          value={posFilterQuery}
          onChange={(e) => {
            setPosFilterQuery(e.target.value);
            setPage(0);
          }}
        />
        <Box
          sx={{
            marginLeft: "1em",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            width: "10em",
          }}
        >
          <Button
            variant={"contained"}
            label={"Add Pos"}
            endIcon={ButtonIcon.add}
            onClick={() => {
              setShowCreateEditPosModal(true);
            }}
          />
        </Box>
      </Box>
      {!pos ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box>
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <button onClick={() => page > 0 && setPage(page - 1)}>&#60;</button>
            <Box sx={{ marginLeft: "1em", marginRight: "1em" }}>
              Page {page + 1} of {numPages}
            </Box>
            <button onClick={() => page < numPages - 1 && setPage(page + 1)}>
              &#62;
            </button>
          </Box>
          <TableContainer>
            <Table size="small" aria-label="a dense table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.name")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.address")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.postcode")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.posType")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.odsCode")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.trust")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.features")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("pages.posManagement.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pos.results.map((pos, index) => (
                  <PosManagementRow
                    pos={pos}
                    refetchPos={refetchPos}
                    key={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {showCreateEditPosModal && (
        <CreateEditPosModal
          open={showCreateEditPosModal}
          onClose={() => {
            setShowCreateEditPosModal(false);
          }}
          refetchPos={refetchPos}
        />
      )}
    </Box>
  );
}
